import Subscriber from '../subscriber.js';
import {
    DATA_TYPES,
    DATA_EVENTS,
    PUSH_CONNECT_RETRY_MAX_COUNT
} from '../constant';
import { extend } from '../../util/jquery.js';
import url from '../../util/url';
import logger from '../../util/logger';
import guid from '../../util/guid';
import { marketsPullQuoteTransform } from '../transforms/markets/pull-quote';

const logModule = 'marketsStream';

export default class MarketsStream extends Subscriber {
    constructor(option) {
        super(option);
        this.option = {
            tryCount: PUSH_CONNECT_RETRY_MAX_COUNT,
            migratoryDataClient:
                typeof window !== 'undefined'
                    ? window.MigratoryDataClient
                    : undefined
        };
        extend(this.option, option);
        this.id = guid();
        this.type = DATA_TYPES.STREAM;
        this.on(DATA_EVENTS.SUBSCRIBE, res => {
            if (res.type === DATA_TYPES.STREAM) {
                this.option.migratoryDataClient.subscribe(
                    this._buildSubscribeParas(res.data)
                );
                this.emit(DATA_EVENTS.AFTER_SUBSCRIBE, res.data);
            }
        }).on(DATA_EVENTS.UNSUBSCRIBE, res => {
            if (res.type === DATA_TYPES.STREAM) {
                this.option.migratoryDataClient.unsubscribe(
                    this._buildSubscribeParas(res.data)
                );
                this.emit(DATA_EVENTS.AFTER_UNSUBSCRIBE, res.data);
            }
        });
    }
    async setOption(option, reconnect) {
        if (
            this.option.sessionId !== option.sessionId ||
            this.option.accessToken !== option.accessToken
        ) {
            extend(this.option, option);
            if (reconnect) {
                const {
                    migratoryDataClient,
                    accessToken,
                    sessionId,
                    env,
                    overridePaths
                } = this.option;
                const subjects = migratoryDataClient
                    .getSubjects()
                    .filter(subject => subject && subject.length > 0);
                migratoryDataClient.disconnect();
                migratoryDataClient.setEntitlementToken(
                    accessToken || sessionId
                );
                this.option.migratoryDataClient.setServers(
                    [
                        url({
                            serviceKey: '@push',
                            env,
                            overridePaths
                        })
                    ],
                    true
                );
                if (subjects.length) {
                    migratoryDataClient.subscribe(subjects);
                }
            }
        }
    }
    async connect() {
        return new Promise((resolve, reject) => {
            const {
                migratoryDataClient,
                accessToken,
                sessionId,
                tryCount,
                env,
                overridePaths
            } = this.option;
            if (typeof migratoryDataClient !== 'undefined') {
                migratoryDataClient.setEntitlementToken(
                    accessToken || sessionId
                );
                // Define the number of failed attempts to connect to one or more MigratoryData servers before triggering a status notification
                migratoryDataClient.notifyAfterReconnectRetries(tryCount);

                migratoryDataClient.setStatusHandler(status => {
                    if (
                        status.type === migratoryDataClient.NOTIFY_SERVER_DOWN
                    ) {
                        // connect failed
                        logger.error(
                            logModule,
                            'Failed to connect push server',
                            status
                        );
                        reject(new Error('Failed to connect push server'));
                    } else if (
                        status.type === migratoryDataClient.NOTIFY_SERVER_UP
                    ) {
                        // connect successful
                        resolve();
                    } else if (
                        status.type ===
                        migratoryDataClient.NOTIFY_SUBSCRIBE_DENY
                    ) {
                        logger.warn(logModule, 'No entitlement', status.info);
                    }
                });

                migratoryDataClient.setMessageHandler(messages => {
                    this.emit(DATA_EVENTS.DATA, {
                        type: this.type,
                        data: marketsPullQuoteTransform(
                            {
                                items: messages.map(m => {
                                    // d.subject /qa/1234/126.1.IBM,
                                    // for instrument end as /, we will append # such as '151.1.AV/  special symbol: 176.1.NDA/FI'
                                    const instrument = m.subject
                                        .replace(/\/\w+\/\d+\/(.+)/, '$1')
                                        .replace(/(.+)#$/, '$1');
                                    return {
                                        ticker: instrument,
                                        source: 'R',
                                        fields: m.fields
                                    };
                                })
                            },
                            {},
                            this.securityManager.quotes.get(DATA_TYPES.STREAM)
                        )
                    });
                });

                migratoryDataClient.setServers(
                    [
                        url({
                            serviceKey: '@push',
                            env,
                            overridePaths
                        })
                    ],
                    true
                );
            } else {
                reject(
                    new Error(
                        'Missed the required dependency, please make sure migratorydata-client is imported when using stream method.'
                    )
                );
            }
        });
    }
    _buildSubscribeParas(instruments) {
        const { env, userId } = this.option;
        return instruments.map(instrument => {
            if (/.+\/$/.test(instrument)) {
                return `/${env}/${userId}/${instrument}#`;
            }
            return `/${env}/${userId}/${instrument}`;
        });
    }
}
